export function expectedAppliedSeq(wave) {
    if (wave.nextOpSeq <= 0)
        return wave.latestSnapshotSeq ?? 0;
    return Math.max(wave.latestSnapshotSeq ?? 0, wave.nextOpSeq - 1);
}
export function emptyWaveSyncDiagnostics() {
    return {
        waveId: null,
        formatVersion: null,
        waveSnapshotSeq: 0,
        waveNextOpSeq: 0,
        appliedSeq: 0,
        expectedLastSeq: 0,
        seqGap: 0,
        tailRequestedAfterSeq: 0,
        lastTailOpsFetched: 0,
        lastTailOpsApplied: 0,
        lastTailOpsSkippedLegacy: 0,
        lastTailOpsFailed: 0,
        blocksCount: 0,
        cacheApplied: false,
        cacheAppliedSeq: null,
        cacheRejectedStale: false,
        networkHydrating: false,
        lastPhase: "idle",
        lastReconcileUpdateBytes: 0,
        updatedAt: Date.now(),
    };
}
/** Clear emit error after reconcile only when outbound work is fully drained. */
export function shouldClearEmitErrorAfterReconcile(args) {
    return (!args.hasUnconfirmedLocalDiff &&
        args.pendingOpsCount === 0 &&
        !args.pendingEmit);
}
/** Skip cache write that would regress block count while local edits are still outbound. */
export function shouldSkipPersistDocumentCacheRegression(args) {
    if (!args.hasOutboundWork)
        return false;
    if (args.cachedBlocksCount == null)
        return false;
    return args.blocksCount < args.cachedBlocksCount;
}
/** Align lastSentStateVector only when the cloud has acknowledged all local structs. */
export function shouldAlignSentStateVector(args) {
    return shouldClearEmitErrorAfterReconcile(args);
}
/**
 * Yjs FILE widgets (Y.Map meta) can encode a ~12-byte self-diff even when
 * lastSent equals the current doc state vector — treat SV equality as synced.
 */
export function hasUnconfirmedYjsDiff(args) {
    if (args.lastSentStateVectorB64 != null &&
        args.lastSentStateVectorB64 === args.docStateVectorB64) {
        return false;
    }
    return args.diffByteLength > 2;
}
/** Heal truncated cache merge only when reconcile lost blocks vs cache at hydration. */
export function shouldReconcileHealTruncatedCache(args) {
    return (!args.replaceDoc &&
        args.attempt === 1 &&
        !args.reconcileHealUsed &&
        args.cacheBlocksAtApply != null &&
        args.updateBytes < 256 &&
        args.blocksAfter < args.cacheBlocksAtApply);
}
/** Merge authoritative server cursors from subscription/poll/reconcile (monotonic). */
export function mergeServerCursors(current, patch) {
    let snapshotSeq = current.snapshotSeq;
    let nextOpSeq = current.nextOpSeq;
    if (patch.snapshotSeq != null && patch.snapshotSeq > 0) {
        snapshotSeq = Math.max(snapshotSeq, patch.snapshotSeq);
    }
    if (patch.nextOpSeq != null && patch.nextOpSeq > 0) {
        nextOpSeq = Math.max(nextOpSeq, patch.nextOpSeq);
    }
    if (patch.maxAppliedSeq != null && patch.maxAppliedSeq > 0) {
        nextOpSeq = Math.max(nextOpSeq, patch.maxAppliedSeq + 1);
    }
    return { snapshotSeq, nextOpSeq };
}
/** Safety reconcile: passive receivers every tick; active clients when work or periodic fallback. */
export function shouldRunSafetyReconcile(args) {
    if (args.needsReconcileWork)
        return true;
    // Receiver passivo: reconcile ogni safety tick (~15s), come pre-refactor.
    if (!args.hasOutboundWork)
        return true;
    if (args.safetyTick > 0 &&
        args.idleFallbackEveryNTicks > 0 &&
        args.safetyTick % args.idleFallbackEveryNTicks === 0) {
        return true;
    }
    return false;
}
/**
 * Detect a hole in inbound op seqs vs the applied cursor (fast-path guard).
 * Contiguous batches (e.g. applied=4, ops [5,6,7]) must NOT report a gap:
 * the cursor advances op by op while scanning.
 */
export function hasInboundSeqGap(args) {
    let lastSeq = args.appliedSeq;
    for (const op of args.sortedOps) {
        if (args.seenOpIds.has(op.opId))
            continue;
        if (lastSeq > 0 && op.seq > lastSeq + 1)
            return true;
        lastSeq = Math.max(lastSeq, op.seq);
    }
    return false;
}
/** After a flush attempt: retry with backoff while the emit error persists; never tight-loop. */
export function nextFlushRetryAction(args) {
    if (!args.pendingEmit)
        return "none";
    if (!args.hasEmitError)
        return "flush-now";
    return args.browserOnline ? "retry-backoff" : "wait-online";
}
/** Ops were applied to Yjs but document materialization did not advance (pending structs). */
export function shouldHealPendingStructs(args) {
    return (args.yjsOpsApplied > 0 &&
        args.revisionBefore === args.revisionAfter);
}
export function formatWaveSyncDiagnostics(d) {
    const lines = [
        `waveId=${d.waveId ?? "—"}`,
        `format=${d.formatVersion ?? "—"}`,
        `snapshotSeq=${d.waveSnapshotSeq}`,
        `nextOpSeq=${d.waveNextOpSeq}`,
        `expectedLastSeq=${d.expectedLastSeq}`,
        `appliedSeq=${d.appliedSeq}`,
        `seqGap=${d.seqGap}`,
        `tail afterSeq=${d.tailRequestedAfterSeq} fetched=${d.lastTailOpsFetched} applied=${d.lastTailOpsApplied} skippedLegacy=${d.lastTailOpsSkippedLegacy} failed=${d.lastTailOpsFailed}`,
        `blocks=${d.blocksCount}`,
        `cache applied=${d.cacheApplied} seq=${d.cacheAppliedSeq ?? "—"} rejectedStale=${d.cacheRejectedStale}`,
        `hydrating=${d.networkHydrating} phase=${d.lastPhase}`,
        `reconcileBytes=${d.lastReconcileUpdateBytes}`,
    ];
    return lines.join("\n");
}
